import { useState } from "react";
import { AlertTriangle, CheckCircle2, Pencil, RefreshCw, ImageIcon } from "lucide-react";

import { GalleryGrid } from "@/components/GalleryGrid";
import { LayoutPicker } from "@/components/LayoutPicker";
import { TexturePanel, IdeaBrief } from "@/components/MoodboardExtras";

function ConsistencyBadge({ check, index }) {
  if (!check) return null;
  const ok = check.status === "consistent";
  const conflict = check.status === "conflict";
  const tone = ok ? "border-emerald-300 bg-emerald-50 text-emerald-900" : conflict ? "border-red-300 bg-red-50 text-red-900" : "border-amber-400 bg-amber-50 text-amber-900";
  return (
    <div data-testid={`consistency-flag-${index}`} className={`border px-4 py-3 text-xs mb-6 ${tone}`}>
      <div className="flex items-center gap-2 font-mono-lab uppercase tracking-wider">
        {ok ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
        {check.status} · DNA match {check.score}%
      </div>
      {check.issues?.length > 0 && <ul className="mt-2 space-y-1 list-disc pl-4">{check.issues.map((s) => <li key={s}>{s}</li>)}</ul>}
      {check.suggestion && <div className="mt-2 italic">{check.suggestion}</div>}
    </div>
  );
}

function EditableText({ value, onSave, testId, className, multiline = true }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  if (editing) {
    return (
      <div className="mb-2">
        <textarea data-testid={`${testId}-input`} value={draft} onChange={e => setDraft(e.target.value)} rows={multiline ? 4 : 1}
          className="w-full text-sm border border-stone-300 p-3 focus:outline-none focus:border-black bg-white" />
        <div className="flex gap-2 mt-2">
          <button data-testid={`${testId}-save`} onClick={() => { onSave(draft); setEditing(false); }} className="px-3 py-1.5 bg-black text-white text-xs">Save</button>
          <button data-testid={`${testId}-cancel`} onClick={() => { setDraft(value || ""); setEditing(false); }} className="px-3 py-1.5 border border-stone-300 text-xs">Cancel</button>
        </div>
      </div>
    );
  }
  return (
    <button data-testid={`${testId}-edit`} onClick={() => { setDraft(value || ""); setEditing(true); }} className={`group text-left w-full ${className}`}>
      {value}
      <Pencil size={12} className="inline ml-2 opacity-0 group-hover:opacity-60 transition-opacity" />
    </button>
  );
}

export function MoodboardCard({ index: i, mb, project, check, regenerating, imageBusy, busyTile, onRegen, onRegenImage, onRegenTile, onSave }) {
  const layout = mb.layout || "hero";
  const count = mb.image_count || 3;
  return (
    <div className="grid grid-cols-12 gap-6" data-testid={`moodboard-room-${i}`}>
      <div className="col-span-12 lg:col-span-4">
        <div className="eyebrow mb-2">{project?.discipline === "Architecture" ? "Space" : "Room"} {String(i + 1).padStart(2, "0")}</div>
        <h2 className="editorial-heading text-3xl mb-4">{mb.room}</h2>
        <ConsistencyBadge check={check} index={i} />
        <EditableText value={mb.design_intent} testId={`moodboard-intent-${i}`} onSave={(v) => onSave({ design_intent: v })} className="text-sm text-stone-600 leading-relaxed mb-6 hover:bg-stone-50" />
        <div className="mb-6">
          <div className="eyebrow mb-2">Materials</div>
          <div className="text-sm space-y-1">
            <div><span className="text-stone-400">Primary:</span> {mb.materials?.primary}</div>
            <div><span className="text-stone-400">Secondary:</span> {mb.materials?.secondary}</div>
            <div><span className="text-stone-400">Accent:</span> {mb.materials?.accent}</div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-1 mb-6">
          {(mb.palette || []).slice(0, 4).map((p) => (<div key={`${p.hex}-${p.name}`} className="h-12" style={{ background: p.hex }} title={p.name} />))}
        </div>
        <TexturePanel textures={mb.textures} index={i} />
        <div className="border-l-2 border-stone-300 pl-3">
          <EditableText value={mb.design_notes} testId={`moodboard-notes-${i}`} onSave={(v) => onSave({ design_notes: v })} className="text-xs text-stone-500 italic hover:bg-stone-50" />
        </div>
        <div className="mt-6 flex flex-wrap gap-2">
          <button onClick={() => onRegen(undefined)} disabled={regenerating} data-testid={`moodboard-regen-${i}`} className="inline-flex items-center gap-2 text-sm border border-stone-300 px-4 py-2 hover:border-black disabled:opacity-50">
            <RefreshCw size={14} className={regenerating ? "animate-spin" : ""} /> {regenerating ? "Regenerating…" : "Regenerate Room"}
          </button>
          <button onClick={onRegenImage} disabled={imageBusy} data-testid={`moodboard-regen-image-${i}`} className="inline-flex items-center gap-2 text-sm border border-stone-300 px-4 py-2 hover:border-black disabled:opacity-50">
            <ImageIcon size={14} className={imageBusy ? "animate-pulse" : ""} /> {imageBusy ? "Rendering…" : "New Lead Image"}
          </button>
        </div>
      </div>
      <div className="col-span-12 lg:col-span-8 space-y-6">
        <IdeaBrief index={i} value={mb.brief} busy={regenerating} onRegenerate={(brief) => onRegen(brief)} />
        <GalleryGrid mb={{ ...mb, layout, image_count: count }} project={project} index={i} imageBusy={imageBusy} busyTile={busyTile} onReorder={(gallery) => onSave({ gallery })} onRegenTile={onRegenTile} />
        <LayoutPicker index={i} layout={layout} count={count} onChange={(patch) => onSave(patch)} />
      </div>
    </div>
  );
}
